import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Zap, Heart, Share2, Award, Sparkles, Target } from "lucide-react";
import { toast } from "sonner";

const API_V1 = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

const CATEGORY_META = {
  profile:   { label: "Profile",      icon: "👤", color: "#00E5FF" },
  content:   { label: "Content & Media", icon: "🎬", color: "#7C3AED" },
  lead:      { label: "Lead Conversion", icon: "📣", color: "#10B981" },
  trust:     { label: "Trust & Compliance", icon: "🛡️", color: "#F59E0B" },
  community: { label: "Community",     icon: "🐎", color: "#EC4899" },
};

export default function ListingHorsepowerSection({ slug, businessName }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cheering, setCheering] = useState(false);
  const [cheered, setCheered] = useState(false);

  useEffect(() => {
    if (!slug) return;
    axios.get(`${API_V1}/horsepower/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const onCheer = async () => {
    if (cheering || cheered) return;
    setCheering(true);
    try {
      const r = await axios.post(`${API_V1}/horsepower/${slug}/cheer`, {});
      setData((prev) => prev ? { ...prev, cheers_count: r.data.cheers_count } : prev);
      setCheered(true);
      toast.success(r.data.already_cheered ? "You've already cheered this listing!" : `🐎 You cheered ${businessName || "this business"}!`);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "";
      console.error("Cheer failed:", err?.response?.status, msg);
      toast.error(msg ? `Cheer failed: ${msg}` : "Could not send cheer right now.");
    } finally {
      setCheering(false);
    }
  };

  if (loading) {
    return (
      <div className="glass-card rounded-2xl p-6 animate-pulse" data-testid="horsepower-loading">
        <div className="h-6 w-2/3 bg-white/10 rounded mb-4" />
        <div className="h-32 w-full bg-white/5 rounded" />
      </div>
    );
  }
  if (!data) return null;

  const { score, level, categories, cheers_count, badges, challenge } = data;
  const challengeGoal = challenge?.goal || 100;
  const challengeEnabled = !!challenge?.enabled;
  const cheersPct = Math.min(100, Math.round((cheers_count / Math.max(1, challengeGoal)) * 100));

  // Build a circular progress for the score
  const radius = 38;
  const circumference = 2 * Math.PI * radius;
  const dash = circumference * (score / 100);

  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.45 }}
      className="glass-card rounded-2xl p-6 sm:p-8 border border-white/10 bg-gradient-to-br from-[#0F172A] via-[#0B1226] to-[#0F172A]"
      data-testid="listing-horsepower-section"
    >
      <div className="flex items-start gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#F59E0B] to-[#EC4899] flex items-center justify-center flex-shrink-0">
          <Zap className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-white font-['Outfit']">Listing Horsepower & Community Momentum</h2>
          <p className="text-sm text-[#94A3B8] mt-1 max-w-2xl">
            Listing Horsepower shows how active and complete this Best of Best Listing is. Community Cheers help support this local business and unlock future rewards, offers, and community campaigns.
          </p>
        </div>
      </div>

      <div className="grid xl:grid-cols-2 gap-6">
        {/* Score circle */}
        <div className="flex items-center gap-4" data-testid="horsepower-score-card">
          <div className="relative flex-shrink-0">
            <svg width="96" height="96" viewBox="0 0 96 96" className="-rotate-90">
              <circle cx="48" cy="48" r={radius} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="8" />
              <motion.circle
                cx="48" cy="48" r={radius}
                fill="none"
                stroke={level.color}
                strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray={`${dash} ${circumference}`}
                initial={{ strokeDasharray: `0 ${circumference}` }}
                whileInView={{ strokeDasharray: `${dash} ${circumference}` }}
                viewport={{ once: true }}
                transition={{ duration: 1, ease: "easeOut" }}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-2xl font-bold text-white leading-none" data-testid="horsepower-score-value">{score}</span>
              <span className="text-[10px] text-[#94A3B8] uppercase tracking-wider">of 100</span>
            </div>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-[#94A3B8] mb-1">Listing Horsepower</p>
            <p className="text-lg font-bold text-white" style={{ color: level.color }} data-testid="horsepower-level-label">
              {level.label}
            </p>
            <p className="text-xs text-[#64748B] mt-1">Based on profile completion, content, AI tools & community engagement.</p>
          </div>
        </div>

        {/* Cheers + Challenge */}
        <div className="flex flex-col justify-center gap-3" data-testid="cheers-block">
          <div>
            <div className="flex items-baseline justify-between mb-1.5">
              <span className="text-sm font-medium text-white">
                {cheers_count} {challengeEnabled ? `/ ${challengeGoal} ` : ""}Community Cheer{cheers_count === 1 ? "" : "s"}
              </span>
              {cheers_count === 0 && <span className="text-xs text-[#94A3B8]">Be the first to cheer 🐎</span>}
            </div>
            <div className="h-2 rounded-full bg-white/5 overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                whileInView={{ width: `${challengeEnabled ? cheersPct : Math.min(100, cheers_count)}%` }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="h-full rounded-full bg-gradient-to-r from-[#F59E0B] to-[#EC4899]"
              />
            </div>
          </div>
          <button
            onClick={onCheer}
            disabled={cheering || cheered}
            className={`w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full font-semibold transition ${
              cheered
                ? "bg-[#EC4899]/15 text-[#EC4899] cursor-default"
                : "bg-gradient-to-r from-[#F59E0B] to-[#EC4899] text-white hover:opacity-90"
            }`}
            data-testid="cheer-button"
          >
            <Heart className={`w-4 h-4 ${cheered ? "fill-[#EC4899]" : ""}`} />
            {cheered ? "You cheered!" : "Cheer for This Business"}
          </button>
        </div>
      </div>

      {/* Category bars — stacks vertically in narrow columns, max 2 cols */}
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-testid="horsepower-categories">
        {Object.entries(categories).map(([key, cat]) => {
          const meta = CATEGORY_META[key] || { label: key, icon: "•", color: "#94A3B8" };
          const pct = Math.round((cat.earned / Math.max(1, cat.total)) * 100);
          return (
            <div key={key} className="rounded-lg bg-white/5 border border-white/10 p-3 min-w-0" data-testid={`hp-cat-${key}`}>
              <div className="flex items-center justify-between gap-2 mb-2 min-w-0">
                <span className="flex items-center gap-1.5 min-w-0">
                  <span className="text-base flex-shrink-0">{meta.icon}</span>
                  <span className="text-xs font-medium text-[#E2E8F0] truncate">{meta.label}</span>
                </span>
                <span className="text-xs font-semibold text-white whitespace-nowrap flex-shrink-0">{cat.earned}<span className="text-[#64748B] font-normal">/{cat.total}</span></span>
              </div>
              <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: meta.color }} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Trust Badges */}
      {badges && badges.length > 0 && (
        <div className="mt-6" data-testid="trust-badges">
          <div className="flex items-center gap-2 mb-3">
            <Award className="w-4 h-4 text-[#F59E0B]" />
            <h3 className="text-sm font-semibold text-white">Trust & Activity Badges</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {badges.map((b) => (
              <span
                key={b.key}
                title={b.description}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F59E0B]/10 border border-[#F59E0B]/30 text-[#FBBF24] text-xs font-medium"
                data-testid={`trust-badge-${b.key}`}
              >
                <Sparkles className="w-3 h-3" />
                {b.label}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Community Challenge block */}
      {challengeEnabled && (
        <div className="mt-6 rounded-xl bg-[#7C3AED]/10 border border-[#7C3AED]/30 p-5" data-testid="community-challenge">
          <div className="flex items-start gap-3">
            <Target className="w-5 h-5 text-[#A78BFA] mt-0.5" />
            <div className="flex-1">
              <h3 className="text-white font-semibold mb-1">{challenge.name || `${challengeGoal}-Cheer Community Challenge`}</h3>
              <p className="text-sm text-[#94A3B8] mb-3">
                {challenge.description || `Help this business reach ${challengeGoal} community cheers and unlock its next LocalBest Rewards or community giveback campaign.`}
              </p>
              {challenge.reward_description && (
                <p className="text-xs text-[#A78BFA] mb-3"><strong>Reward:</strong> {challenge.reward_description}</p>
              )}
              {challenge.disclaimer && (
                <p className="text-[10px] text-[#64748B] italic mb-3">{challenge.disclaimer}</p>
              )}
              <p className="text-[10px] text-[#64748B] italic">
                Eligible campaigns may unlock LocalBest-sponsored rewards, offers, or community giveback opportunities, subject to approval and campaign terms.
              </p>
            </div>
          </div>
        </div>
      )}
    </motion.section>
  );
}
